import { captureException } from '@sentry/react'
import { useCallback, useMemo, useState } from 'react'
import { type HomeDeliveryGuidance } from '../requestedPharmacy'

/**
 * Which Home Delivery guidance strips the provider has pressed "Got it" on, per visit.
 *
 * Product's rule: dismissed stays dismissed for the rest of *that* visit, across refreshes, and
 * the next visit that requests Home Delivery nudges again. So the record is keyed by visit id and
 * kept in localStorage — and by provider too, because a clinic workstation is shared: a visit one
 * provider deferred after pressing "Got it" must still nudge the provider who picks it up. Deliberately **not** in Redux, for the reason `dismissedAnnouncements`
 * gives: `redux-persist` and `redux-state-sync` would put a returning provider's session at risk for
 * a record nothing else reads.
 *
 * The warning is stored apart from the info nudge, and per pickup script
 * (`{visitId}:warning:{prescriptionId}`): it is the "this script went to the wrong pharmacy" signal,
 * so having dismissed the nudge earlier in the visit must not hide it, and "Got it" on one pickup
 * script must not hide the warning for the next one sent in the same visit.
 */

// Namespaced per app, like `providerAnnouncementDismissals`: two QuickMD apps can share an origin
// in a preview environment.
const STORAGE_KEY = 'providerHdNudgeDismissals'

/** A visit is long over by then; the record only has to outlive a refresh or a second tab. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

type Dismissals = Record<string, number>

const isDismissals = (value: unknown): value is Dismissals =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every((at) => typeof at === 'number')

const prune = (entries: Dismissals, now: number): Dismissals =>
  Object.fromEntries(Object.entries(entries).filter(([, at]) => now - at < MAX_AGE_MS))

const read = (): Dismissals => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return isDismissals(parsed) ? prune(parsed, Date.now()) : {}
  } catch {
    // A corrupt or unavailable store costs one re-shown nudge, never a broken visit screen.
    return {}
  }
}

/** The scripts a warning is about, by id. Only the warning carries any; the info nudge is per visit. */
type HdNudge = { kind: 'info' } | { kind: 'warning'; prescriptionId: string }

export const hdNudgeDismissalKey = (providerId: string | null | undefined, visitId: string, nudge: HdNudge): string => {
  const visitKey = nudge.kind === 'warning' ? `${visitId}:warning:${nudge.prescriptionId}` : visitId
  // Before the profile has loaded there is no provider to key on; the visit alone still keeps
  // "Got it" across a refresh.
  return providerId ? `${providerId}:${visitKey}` : visitKey
}

export const isHdNudgeDismissed = (key: string): boolean => key in read()

export const recordHdNudgeDismissal = (key: string): void => {
  try {
    const now = Date.now()
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...read(), [key]: now }))
  } catch (error) {
    captureException(error)
  }
}

/** Test seam. */
export const clearHdNudgeDismissalsForTests = (): void => window.localStorage.removeItem(STORAGE_KEY)

/**
 * `[dismissed, dismiss]` for the strip a visit is currently showing. A warning counts as dismissed
 * only once every script it is about has been. Local state backs up the stored record so "Got it"
 * hides the strip even when storage is unavailable.
 *
 * `pickupPrescriptionIds` are the scripts behind a warning (`sentToPickup`); ignored for the info nudge.
 */
export const useHdNudgeDismissal = (
  providerId: string | null | undefined,
  visitId: string | null | undefined,
  kind: HomeDeliveryGuidance | null,
  pickupPrescriptionIds: readonly string[],
): [boolean, () => void] => {
  // Joined into one string so the memo below keys on a value, not on a new array every render.
  const keyList =
    !visitId || !kind ? ''
    : kind === 'info' ? hdNudgeDismissalKey(providerId, visitId, { kind })
    : [...pickupPrescriptionIds]
        .sort()
        .map((prescriptionId) => hdNudgeDismissalKey(providerId, visitId, { kind, prescriptionId }))
        .join('\n')
  const [dismissedThisSession, setDismissedThisSession] = useState<ReadonlySet<string>>(() => new Set())
  // Read once per key list rather than per render: the popup re-renders on every Daily call event.
  const keys = useMemo(() => (keyList ? keyList.split('\n') : []), [keyList])
  const stored = useMemo(() => new Set(keys.filter(isHdNudgeDismissed)), [keys])

  const dismiss = useCallback(() => {
    keys.forEach(recordHdNudgeDismissal)
    setDismissedThisSession((previous) => new Set([...previous, ...keys]))
  }, [keys])

  return [keys.length > 0 && keys.every((key) => stored.has(key) || dismissedThisSession.has(key)), dismiss]
}
